// frontend/src/stores/auth.ts
// ============================================================================
// RAVERS NETWORK - Pinia Store аутентификации, сессий и профиля пользователя
// ============================================================================

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, UnauthorizedError, MaintenanceError } from '@/api/client';
import type { UserProfileView } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  // ==========================================================================
  // СОСТОЯНИЕ (STATE)
  // ==========================================================================
  const user = ref<UserProfileView | null>(null);
  const isLoading = ref<boolean>(false);
  const isInitialized = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Информация о техническом обслуживании платформы
  const maintenanceInfo = ref<{
    active: boolean;
    message: string;
    hubId?: string;
  }>({
    active: false,
    message: '',
  });

  // ==========================================================================
  // ВЫЧИСЛЯЕМЫЕ СВОЙСТВА (GETTERS)
  // ==========================================================================
  const isAuthenticated = computed(() => !!user.value);
  const userId = computed(() => user.value?.user_id ?? '');
  const userLogin = computed(() => user.value?.login ?? '');
  const userCountry = computed(() => user.value?.country ?? 'ru');
  const userAvatar = computed(() => user.value?.avatar_url ?? null);
  const userPublicKey = computed(() => user.value?.public_key ?? null);

  // ==========================================================================
  // ДЕЙСТВИЯ (ACTIONS)
  // ==========================================================================

  /**
   * Инициализация пользователя при старте приложения
   */
  async function fetchCurrentUser(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      user.value = await api.auth.getMe();
      maintenanceInfo.value.active = false;
      return true;
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        user.value = null;
      } else if (err instanceof MaintenanceError) {
        maintenanceInfo.value = {
          active: true,
          message: err.message,
          hubId: err.hubId,
        };
      } else {
        console.warn('Не удалось загрузить текущего пользователя:', err);
      }
      return false;
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  /**
   * Вход в систему по логину, паролю и стране
   */
  async function login(payload: {
    login: string;
    password: string;
    country: string;
  }): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await api.auth.login(payload);
      // После успешного входа загружаем полный профиль
      await fetchCurrentUser();
    } catch (err: any) {
      error.value = err.message || 'Ошибка авторизации';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Регистрация нового аккаунта
   */
  async function register(payload: {
    login: string;
    password: string;
    country: string;
    recovery_phrase?: string;
    public_key?: string;
    vault_payload?: string;
  }): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await api.auth.register(payload);
      await fetchCurrentUser();
    } catch (err: any) {
      error.value = err.message || 'Ошибка регистрации';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Загрузка нового аватара пользователя
   */
  async function uploadAvatar(file: File): Promise<string> {
    isLoading.value = true;
    error.value = null;

    try {
      const res = await api.auth.uploadAvatar(file);
      if (user.value) {
        user.value.avatar_url = res.avatar_url;
      }
      return res.avatar_url;
    } catch (err: any) {
      error.value = err.message || 'Ошибка загрузки аватара';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Обновление публичного ключа E2EE ECDH
   */
  async function updatePublicKey(pubkey: string): Promise<void> {
    try {
      await api.auth.updateMyPubkey(pubkey);
      if (user.value) {
        user.value.public_key = pubkey;
      }
    } catch (err: any) {
      console.error('Ошибка сохранения публичного ключа:', err);
      throw err;
    }
  }

  /**
   * Выход из аккаунта и сброс состояния
   */
  async function logout(): Promise<void> {
    isLoading.value = true;
    try {
      await api.auth.logout();
    } catch (err) {
      console.warn('Ошибка при вызове logout эндпоинта:', err);
    } finally {
      user.value = null;
      isLoading.value = false;
    }
  }

  return {
    // State & Getters
    user,
    isLoading,
    isInitialized,
    error,
    maintenanceInfo,
    isAuthenticated,
    userId,
    userLogin,
    userCountry,
    userAvatar,
    userPublicKey,

    // Actions
    fetchCurrentUser,
    login,
    register,
    uploadAvatar,
    updatePublicKey,
    logout,
  };
});
